export const companyInfo = {
  name: 'Blossom Prime Facility Solutions Limited',
  shortName: 'Blossom Prime',
  tagline: 'Clean Spaces. Safe Environments. Better Living.',
  description: 'Blossom Prime Facility Solutions provides professional cleaning services across the United Kingdom — helping businesses and organisations maintain clean, safe and welcoming environments.',
  address: {
    street: '98 Ascend Co-working, Pentagon Centre, First Floor',
    city: 'Chatham',
    region: 'Kent',
    postcode: 'ME4 4HW',
    country: 'United Kingdom',
  },
  contact: {
    phone: '07868 039329',
    email: 'info@blossomprimefacilities.co.uk',
    phoneIntl: '+44 7868 039329',
  },
  social: {
    linkedin: 'https://www.linkedin.com/company/blossom-prime-facility-solutions',
  },
  hours: 'Mon–Fri: 8:00 AM – 6:00 PM | Sat: 9:00 AM – 4:00 PM | Sun: By Appointment',
} as const

export const navigation = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Sectors', href: '/sectors' },
  { label: 'Why Us', href: '/why-us' },
  { label: 'Contact', href: '/contact' },
] as const

export const services = [
  {
    id: 'construction-cleaning',
    title: 'Construction Cleaning',
    shortTitle: 'Construction',
    description: 'Supporting contractors and developers from the first builders clean through to handover-ready sparkle cleans. We prepare construction sites and new builds to the highest presentation standards — on time and with minimal disruption.',
    features: [
      'Builders Clean — removal of construction debris, dust, and residue',
      'Sparkle Clean — final polish before handover',
      'Post-renovation cleaning of all surfaces',
      'Window and glass cleaning',
      'Site welfare facility cleaning',
      'Waste removal and disposal',
    ],
    icon: 'Building2',
    color: 'blossom',
    image: '/images/3_Green_Waste_Recycling_Plant_Modern.png',
    stats: {},
  },
  {
    id: 'healthcare-cleaning',
    title: 'Healthcare & Care Home Cleaning',
    shortTitle: 'Healthcare',
    description: 'Specialist, infection-conscious cleaning for care homes and healthcare environments, aligned to the National Standards of Healthcare Cleanliness 2025 and NHS infection prevention best practice.',
    features: [
      'NSoHC 2025 aligned cleaning procedures',
      'Infection control and prevention protocols',
      'Deep cleaning of high-touch points and sanitary areas',
      'Colour-coded equipment to reduce cross-contamination',
      'DBS-checked personnel deployed where required',
      'Regular contract cleaning to maintain day-to-day standards',
    ],
    icon: 'HeartPulse',
    color: 'prime',
    image: '/images/5_Eco_Robot_Over_6_616_Royalty_Free.png',
    stats: {},
  },
  {
    id: 'student-accommodation',
    title: 'Student Accommodation Cleaning',
    shortTitle: 'Student Living',
    description: 'Fast, reliable turnaround and deep-cleaning services for student accommodation and hostels during busy changeover periods between academic sessions.',
    features: [
      'End-of-session comprehensive deep cleans',
      'Room-by-room turnaround cleaning between occupants',
      'Communal area cleaning — kitchens, lounges, corridors',
      'Kitchen and bathroom deep cleans',
      'Flexible scheduling around academic term dates',
      'Hostel and shared accommodation services',
    ],
    icon: 'GraduationCap',
    color: 'accent',
    image: '/images/6_Facility_Management_PNGs_for_Free.png',
    stats: {},
  },
  {
    id: 'property-management',
    title: 'Property Management & Lettings Cleaning',
    shortTitle: 'Property',
    description: 'Helping letting agents, landlords and property managers prepare, turn around and maintain residential and commercial properties to a consistently high standard.',
    features: [
      'End-of-tenancy cleaning to deposit-return standard',
      'Void property deep cleans',
      'Pre-tenancy preparation and presentation',
      'Regular communal area maintenance contracts',
      'Short-let and Airbnb changeover services',
      'Emergency and same-day cleaning for urgent needs',
    ],
    icon: 'Home',
    color: 'blossom',
    image: '/images/2_3_827_Futuristic_Green_Building_Stock.png',
    stats: {},
  },
] as const

export const sectors = [
  {
    id: 'construction',
    title: 'Construction & Development',
    description: 'From small renovations to large-scale new builds, we support contractors and developers with builders cleans, sparkle cleans and site welfare cleaning — prepared to the standard required at every stage.',
    image: '/images/3_Green_Waste_Recycling_Plant_Modern.png',
    stats: {},
  },
  {
    id: 'healthcare',
    title: 'Healthcare & Social Care',
    description: 'Specialist cleaning for care homes, NHS environments and healthcare facilities. Our procedures are aligned to infection prevention best practice and the National Standards of Healthcare Cleanliness 2025.',
    image: '/images/1_Robot_Hand_Saving_Earth_with_Crystal.png',
    stats: {},
  },
  {
    id: 'education',
    title: 'Education & Student Living',
    description: 'Reliable, fast-turnaround cleaning for university halls, purpose-built student accommodation and hostels — managing busy changeover windows to get rooms ready for the next intake.',
    image: '/images/4_Facility_Management_Logo_Vector_Art.png',
    stats: {},
  },
  {
    id: 'property',
    title: 'Property & Lettings',
    description: 'End-to-end cleaning for estate agents, letting agents and property management companies — from end-of-tenancy cleans and communal areas to emergency turnarounds.',
    image: '/images/2_3_827_Futuristic_Green_Building_Stock.png',
    stats: {},
  },
  {
    id: 'commercial',
    title: 'Commercial & Corporate',
    description: 'Professional office and commercial space cleaning scheduled around your working hours — flexible, consistent and designed to keep your environment presentable at all times.',
    image: '/images/7_Future_eco_friendly_city_illustration.png',
    stats: {},
  },
  {
    id: 'hospitality',
    title: 'Hospitality & Leisure',
    description: 'Thorough, reliable cleaning for hotels, restaurants and leisure venues — supporting the presentation standards your guests expect, delivered on a schedule that works for your operation.',
    image: '/images/8_Electric_Chip_and_Green_Leaves_Blending.png',
    stats: {},
  },
] as const

export const whyUs = {
  stats: [
    { value: '4', label: 'Core Sectors Served', suffix: '' },
    { value: '24h', label: 'Quote Response Time', suffix: '' },
    { value: 'UK', label: 'Nationwide Coverage', suffix: '' },
    { value: '100%', label: 'Insurance Coverage', suffix: '' },
  ],
  differentiators: [
    {
      title: 'Eco-Friendly Approach',
      description: 'We use environmentally responsible cleaning products and work to minimise waste, packaging and environmental impact across all our operations.',
      icon: 'Leaf',
    },
    {
      title: 'Structured Quality Control',
      description: 'Checklists, supervision and inspections confirm every job is completed to specification. We use digital records to maintain consistency and accountability.',
      icon: 'CheckCircle',
    },
    {
      title: 'Fully Insured & Compliant',
      description: 'Public and Employers Liability insurance in place. DBS checks available where required. Staff trained to HSE and relevant industry cleaning standards.',
      icon: 'ShieldCheck',
    },
    {
      title: 'Flexible Scheduling',
      description: 'We work around your operation — early mornings, evenings and weekends accommodated. Access, timescales and scope are agreed upfront so there are no surprises.',
      icon: 'Clock',
    },
    {
      title: 'Clear Communication',
      description: 'We respond to quote requests within 24 hours and keep clients informed throughout the job. No hidden costs, no shortcuts — just straightforward service.',
      icon: 'MessageSquare',
    },
    {
      title: 'Responsible Practices',
      description: 'We support healthier indoor environments by choosing safer products, reducing chemical usage and working in a way that protects people and the spaces they use.',
      icon: 'Recycle',
    },
  ],
  process: [
    {
      step: '01',
      title: 'Understanding',
      description: 'We learn your site, standards and timescales, then provide a clear, transparent quotation with no hidden costs.',
      icon: 'MessageSquare',
    },
    {
      step: '02',
      title: 'Planning',
      description: 'We agree scope, schedules and access — preparing the right team, equipment and products before work begins.',
      icon: 'FileText',
    },
    {
      step: '03',
      title: 'Delivery',
      description: 'Trained operatives follow defined methods and safe systems of work on every site, every time.',
      icon: 'Users',
    },
    {
      step: '04',
      title: 'Quality Check',
      description: 'Checklists, supervision and inspections confirm work is completed to specification before we sign off.',
      icon: 'CheckCircle',
    },
  ],
  testimonials: [] as const,
  certifications: [
    { name: 'BICSc', description: 'British Institute of Cleaning Science' },
    { name: 'CHAS / SafeContractor', description: 'Health & Safety Accreditation' },
    { name: 'CSCS', description: 'Construction Skills Certification Scheme' },
    { name: 'ISO 9001', description: 'Quality Management' },
    { name: 'ISO 14001', description: 'Environmental Management' },
    { name: 'ISO 45001', description: 'Health & Safety Management' },
    { name: 'COSHH / HSE', description: 'Hazardous Substances Guidance' },
    { name: 'NSoHC 2025', description: 'National Standards of Healthcare Cleanliness' },
  ],
} as const

export const aboutContent = {
  mission: 'To provide dependable, professional and high-quality cleaning services that help our clients maintain clean, safe and welcoming environments — delivered consistently, responsibly and to the highest industry standards.',
  vision: 'To be recognised across the United Kingdom as a trusted, standards-led cleaning partner of choice — known for the quality of our work, the professionalism of our people and the safety of the environments we help create.',
  values: [
    {
      title: 'Quality',
      description: 'We hold ourselves to high standards and pay close attention to detail on every job — regardless of size or sector.',
      icon: 'CheckCircle',
    },
    {
      title: 'Reliability',
      description: 'We do what we say we will, when we say we will. We turn up on time, prepared, and we see the job through.',
      icon: 'Clock',
    },
    {
      title: 'Safety',
      description: 'We protect our clients, our people and the public through safe, compliant working practices on every site.',
      icon: 'Shield',
    },
    {
      title: 'Integrity',
      description: 'We are honest, transparent and accountable in everything we do — from our quotations to the way we work.',
      icon: 'ShieldCheck',
    },
    {
      title: 'Respect',
      description: "We treat every client's property, staff and residents with care and professionalism at all times.",
      icon: 'Users',
    },
    {
      title: 'Responsibility',
      description: 'We work in an environmentally responsible way, using safer products and supporting healthier spaces and a cleaner environment.',
      icon: 'Leaf',
    },
  ],
} as const

export const contactFormFields = [
  { name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'John Smith' },
  { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'john@company.co.uk' },
  { name: 'phone', label: 'Phone Number', type: 'tel', required: false, placeholder: '07868 039329' },
  { name: 'company', label: 'Company / Organisation', type: 'text', required: false, placeholder: 'Your company name' },
  { name: 'service', label: 'Service Required', type: 'select', required: true, options: [
    'Construction Cleaning',
    'Healthcare & Care Home Cleaning',
    'Student Accommodation Cleaning',
    'Property Management & Lettings Cleaning',
    'Commercial Cleaning',
    'Other',
  ]},
  { name: 'message', label: 'Message', type: 'textarea', required: true, placeholder: 'Tell us about your project, timeline, and any specific requirements...' },
] as const

export const seoMeta = {
  home: {
    title: 'Blossom Prime Facility Solutions | Professional Cleaning Services UK',
    description: 'Professional cleaning services across the UK — construction, healthcare, student accommodation and property management. Fully insured, BICSc-aligned, UK nationwide.',
  },
  about: {
    title: 'About Us | Blossom Prime Facility Solutions',
    description: 'Learn about Blossom Prime Facility Solutions — our mission, values and commitment to professional, dependable cleaning services across the UK.',
  },
  services: {
    title: 'Our Services | Blossom Prime Facility Solutions',
    description: 'Professional cleaning services for construction, healthcare, student accommodation and property management across the UK.',
  },
  sectors: {
    title: 'Industries We Serve | Blossom Prime Facility Solutions',
    description: 'Specialist cleaning for construction, healthcare, education, property, commercial and hospitality sectors across the UK.',
  },
  whyUs: {
    title: 'Why Choose Us | Blossom Prime Facility Solutions',
    description: 'Fully insured, BICSc-aligned and quality-assured — discover why clients across the UK choose Blossom Prime for their cleaning needs.',
  },
  contact: {
    title: 'Contact Us | Blossom Prime Facility Solutions',
    description: 'Get in touch with Blossom Prime Facility Solutions for a free quote. Call 07868 039329 or email info@blossomprimefacilities.co.uk.',
  },
} as const
